import { useState } from "react";
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import HeightOutlinedIcon from "@mui/icons-material/HeightOutlined";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RhDashboardCell } from "./RhDashboardCell";

export const ReportBuilderColumn = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const stop = (e) => e.stopPropagation();

    // Drag-to-resize shared by both edge handles; keeps the column's flex width in whole steps.
    // dir flips the drag direction per edge: right edge grows when dragged right (+1),
    // left edge grows when dragged left (-1), so each border resizes outward.
    const startResize = (e, dir) => {
        // Keep that edge's surface visible for the whole drag, even if the cursor slips off the hit zone.
        setResizingEdge(dir < 0 ? "left" : "right");
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = props.column.width;

        const onMouseMove = (moveEvent) => {
            const delta = (moveEvent.clientX - startX) * dir;
            const newWidth = Math.max(1, startWidth + Math.round(delta / 50));
            props.handleResizeColumn(props.containerKey, props.si, props.ri, props.ci, newWidth, props.di);
        };

        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            setResizingEdge(null);
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    };
    const [hoverEdge, setHoverEdge] = useState(null);
    const [resizingEdge, setResizingEdge] = useState(null);

    // Narrow draggable hit zone pinned to a column edge; hovering it reveals that side's
    // curved border surface (below) and lets you drag to resize.
    const edgeHitSx = {
        position: "absolute",
        top: 0,
        bottom: 0,
        width: 24,
        cursor: "ew-resize",
        zIndex: 3,
    };
    // Card-shaped overlay with a full rounded border; each instance is clip-pathed down to one
    // edge strip (see JSX), so you see that side plus its curved corners. The matching
    // border-radius is what makes the stroke arc smoothly around the corner instead of mitering.
    const edgeSurfaceSx = {
        position: "absolute",
        inset: 0,
        borderRadius: 4,
        border: `8px solid ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[300]}`,
        pointerEvents: "none",
        transition: "opacity 120ms ease",
        zIndex: 2,
    };
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: props.column.id,
        data: { type: "column" },
        disabled: props.dragDisabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.2 : 1,
        // backgroundColor: "#e3f2fd",
        // border: "1px solid #90caf9",
        minWidth: 120,
        textAlign: "center",
        cursor: props.dragDisabled ? "default" : props.isOverlay ? "grabbing" : "grab",
        boxShadow: isDragging ? "0 10px 20px rgba(0,0,0,0.2)" : "none",
    };

    return (

        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            sx={{
                flex: props.column.width ?? 1,
                minHeight: 200,
                py: 2,
                px: 3,
                borderRadius: 4,
                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                position: "relative",
                display: "flex",
                justifyContent: "space-between",
                "& .resize-column-btn": {
                    display: "none"
                },
                "& .remove-column-btn": {
                    display: "none"
                },
                "&:hover": {
                    " .resize-column-btn": {
                        display: "flex"
                    },
                    " .remove-column-btn": {
                        display: "flex"
                    }
                },
            }}
            style={style}
        >
            <RhDashboardCell
                chart={props.column.chart}
                onPick={() => props.onOpenReportPicker?.(props.column.id, props.allowFullPivot)}
                onClear={() => props.handleSetColumnChart?.(props.column.id, {})}
                handleRemoveColumn={props.handleRemoveColumn}
                containerKey={props.containerKey}
                si={props.si}
                ri={props.ri}
                ci={props.ci}
                di={props.di}
            />

            {/* <ButtonBase
                className="remove-column-btn"

                onPointerDown={(e) => {
                    e.stopPropagation();
                }}
                onClick={() => {
                    if(props.isTopRow){
                        props.handleRemoveColumnFromTopRow(props.si,props.ri,props.ci);
                    }else {
                        props.handleRemoveColumn(props.tabId, props.si, props.ri, props.ci, props.di);
                    }
                }}
                sx={{
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    borderRadius: 4,
                    background: colors.red[100],
                    color: colors.red[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                        background: colors.red[200]
                    }
                }}
            >
                x
            </ButtonBase> */}

            {/* Curved border surfaces — full rounded border clipped to one edge strip so the
                stroke curves around that side's corners. Each shows while its edge is hovered. */}
            <Box sx={{ ...edgeSurfaceSx, clipPath: "inset(0 calc(100% - 10px) 0 0)", opacity: (hoverEdge === "left" || resizingEdge === "left") ? 1 : 0 }} />
            <Box sx={{ ...edgeSurfaceSx, clipPath: "inset(0 0 0 calc(100% - 10px))", opacity: (hoverEdge === "right" || resizingEdge === "right") ? 1 : 0 }} />

            {/* Drag hit zones on both edges — drag to grow or shrink the column. */}
            <Box
                sx={{ ...edgeHitSx, left: 0 }}
                onMouseEnter={() => setHoverEdge("left")}
                onMouseLeave={() => setHoverEdge(null)}
                onPointerDown={stop}
                onMouseDown={(e) => startResize(e, -1)}
            />
            <Box
                sx={{ ...edgeHitSx, right: 0 }}
                onMouseEnter={() => setHoverEdge("right")}
                onMouseLeave={() => setHoverEdge(null)}
                onPointerDown={stop}
                onMouseDown={(e) => startResize(e, 1)}
            />
        </Box>
        // <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        //     <strong>Column</strong><br />
        //     ID: {column.id}<br />
        //     Width: {column.width}
        // </div>
    );
};